/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import ProjectReducer from "reducers/ProjectReducer";
import PlayerReducer, { PlayerAction } from "reducers/PlayerReducer";
import AssetsReducer from "reducers/AssetsReducer";
import RecorderReducer from "reducers/RecorderReducer";
import ParticipantsReducer from "reducers/ParticipantsReducer";
import SelectionReducer from "reducers/SelectionReducer";
import PasteBufferReducer from "reducers/PasteBufferReducer";
import ClipsReducer from "reducers/ClipsReducer";
import DeviceSettingsReducer from "reducers/DeviceSettingsReducer";
import {
  ACCEPT_SESSION_INTERSTITIAL,
  RESET_PROJECT_STATE,
  SET_MUSIC_COLLECTION_CURRENTLY_PREVIEWING_TRACK_URL,
  SET_SERVER_TIME_OFFSET_MS,
  SET_SPEAKER_DIARIZATION_ELIGIBLE,
  SHOW_INVITE_GUESTS_POPUP,
} from "constants/ActionTypes";
import DragAndDropReducer from "reducers/DragAndDropReducer";
import CurrentUserReducer from "reducers/CurrentUserReducer";
import ToastMessagesReducer from "reducers/ToastMessagesReducer";
import { INITIAL_STATE } from "reducers/InitialState";
import RecordingSessionsReducer from "reducers/RecordingSessionsReducer";
import { behanceApi } from "api/BehanceApi";
import { AppState } from "types/AppState";
import { Action } from "types/AppAction";
import {
  AssetsAction,
  AssetUploadAction,
  ClipsAction,
  CurrentUserActions,
  DeviceAction,
  DragAndDropAction,
  ParticipantAction,
  ProjectAction,
  RecorderAction,
  RecordingSessionActions,
  SelectionAction,
  SetPasteBufferAction,
  ToastMessageActions,
} from "types/Actions";
import { waveformApi } from "api/WaveformApi";
import AssetUploadReducer from "./AssetUploadReducer";

export default (state: AppState, action: Action): AppState => {
  switch (action.type) {
    case ACCEPT_SESSION_INTERSTITIAL:
      return {
        ...state,
        currentUser:
          state.currentUser === null
            ? null
            : {
                ...state.currentUser,
                acceptedInterstitial: true,
              },
      };
    case SET_SERVER_TIME_OFFSET_MS:
      return {
        ...state,
        serverTimeOffsetMs: action.serverTimeOffsetMs,
      };
    case SET_MUSIC_COLLECTION_CURRENTLY_PREVIEWING_TRACK_URL:
      return {
        ...state,
        musicCollection: { currentlyPreviewingTrackUrl: action.trackUrl },
      };
    case SET_SPEAKER_DIARIZATION_ELIGIBLE:
      return {
        ...state,
        speakerDiarizationEligible: action.eligible,
      };
    case SHOW_INVITE_GUESTS_POPUP:
      return {
        ...state,
        inviteGuestsPopupOpen: action.inviteGuestsPopupOpen,
      };
    case RESET_PROJECT_STATE:
      return {
        ...INITIAL_STATE,
        currentUser: state.currentUser,
        currentUserLoaded: state.currentUserLoaded,
        toastMessages: state.toastMessages,
        deviceSettings: state.deviceSettings,
      };

    default:
      return {
        ...CurrentUserReducer(state, action as CurrentUserActions),
        clips: ClipsReducer(state.clips, action as ClipsAction),
        project: ProjectReducer(state.project, action as ProjectAction),
        player: PlayerReducer(state.player, action as PlayerAction),
        assets: AssetsReducer(state.assets, action as AssetsAction),
        assetUploads: AssetUploadReducer(
          state.assetUploads,
          action as AssetUploadAction
        ),
        recorder: RecorderReducer(state.recorder, action as RecorderAction),
        deviceSettings: DeviceSettingsReducer(
          state.deviceSettings,
          action as DeviceAction
        ),
        participants: ParticipantsReducer(
          state.participants,
          action as ParticipantAction
        ),
        [behanceApi.reducerPath]: behanceApi.reducer(
          state[behanceApi.reducerPath],
          action
        ),
        [waveformApi.reducerPath]: waveformApi.reducer(
          state[waveformApi.reducerPath],
          action
        ),
        recordingSessions: RecordingSessionsReducer(
          state.recordingSessions,
          action as RecordingSessionActions
        ),
        selection: SelectionReducer(state.selection, action as SelectionAction),
        pasteBuffer: PasteBufferReducer(
          state.pasteBuffer,
          action as SetPasteBufferAction
        ),
        dragAndDrop: DragAndDropReducer(
          state.dragAndDrop,
          action as DragAndDropAction
        ),
        toastMessages: ToastMessagesReducer(
          state.toastMessages,
          action as ToastMessageActions
        ),
        musicCollection: state.musicCollection,
        serverTimeOffsetMs: state.serverTimeOffsetMs,
        inviteGuestsPopupOpen: state.inviteGuestsPopupOpen,
        speakerDiarizationEligible: state.speakerDiarizationEligible,
      };
  }
};
